import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle, 
  Receipt, 
  PiggyBank, 
  Wrench, 
  RefreshCw, 
  ShieldCheck, 
  Calculator,
  Phone
} from 'lucide-react';
import AnimatedSection from '../components/common/AnimatedSection';
import ContactForm from '../components/common/ContactForm';
import SEOHead from '../components/common/SEOHead';
import { companyInfo, rentalBenefits, products, heroImages } from '../data/mock';

const iconMap = {
  Receipt,
  PiggyBank,
  Wrench,
  RefreshCw,
  ShieldCheck,
  Calculator
};

const RentalPage = () => {
  const rentalProducts = products.filter(p => p.subcategory === 'diesel').slice(0, 4);

  return (
    <>
      <SEOHead 
        title="Locação de Empilhadeiras"
        description="Aluguel de empilhadeiras GLP e Diesel com capacidade de 2,5t a 7,0t. Contratos flexíveis: diária, mensal ou anual. Manutenção inclusa."
        keywords="locação empilhadeira, aluguel empilhadeira, empilhadeira mensal, empilhadeira diária"
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900/30 py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="fadeRight">
              <span className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                Flexibilidade Total
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Locação de
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-teal-500"> Empilhadeiras</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Sistema de locação compatível com a necessidade de cada cliente. 
                Opções de contratos diários, mensais ou anuais.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={`https://wa.me/${companyInfo.whatsapp}?text=Olá! Gostaria de um orçamento de locação de empilhadeira.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
                >
                  Solicitar Orçamento
                  <ArrowRight size={20} />
                </a>
              </div>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeLeft" className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <h3 className="text-white text-xl font-semibold mb-6">Disponibilidade</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-white">
                    <CheckCircle className="text-teal-400" />
                    <span>Empilhadeiras GLP</span>
                  </div>
                  <div className="flex items-center gap-4 text-white">
                    <CheckCircle className="text-teal-400" />
                    <span>Empilhadeiras Diesel</span>
                  </div>
                  <div className="flex items-center gap-4 text-white">
                    <CheckCircle className="text-teal-400" />
                    <span>Capacidade de 2,5t a 7,0t</span>
                  </div>
                  <div className="flex items-center gap-4 text-white">
                    <CheckCircle className="text-teal-400" />
                    <span>Contratos flexíveis</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="text-teal-500 font-semibold uppercase tracking-wider">Vantagens</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-4">
              Por Que Alugar?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Descubra as vantagens de optar pela locação de empilhadeiras.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rentalBenefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon] || CheckCircle;
              return (
                <AnimatedSection key={benefit.id} animation="fadeUp" delay={index * 100}>
                  <div className="group p-8 bg-gray-50 rounded-2xl hover:bg-white hover:shadow-xl border border-transparent hover:border-teal-200 transition-all duration-500">
                    <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-teal-500 transition-colors duration-500">
                      <IconComponent size={28} className="text-teal-600 group-hover:text-white transition-colors duration-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Available Equipment */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="text-teal-500 font-semibold uppercase tracking-wider">Equipamentos</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-4">
              Disponíveis para Locação
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {rentalProducts.map((product, index) => (
              <AnimatedSection key={product.id} animation="fadeUp" delay={index * 100}>
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2">{product.name}</h3>
                    <p className="text-sm text-gray-500 mb-2">Modelo: {product.model}</p>
                    <p className="text-teal-600 font-semibold">{product.capacity}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection animation="fadeUp" delay={400} className="text-center mt-12">
            <Link
              to="/produtos"
              className="inline-flex items-center gap-2 text-teal-600 font-semibold hover:underline"
            >
              Ver todos os equipamentos
              <ArrowRight size={20} />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Contract Types */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="text-teal-400 font-semibold uppercase tracking-wider">Modalidades</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-4">Tipos de Contrato</h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Diária', description: 'Ideal para necessidades pontuais e projetos de curta duração.', highlight: false },
              { title: 'Mensal', description: 'Perfeito para demandas sazonais ou projetos de médio prazo.', highlight: true },
              { title: 'Anual', description: 'Melhor custo-benefício para operações contínuas.', highlight: false }
            ].map((plan, index) => (
              <AnimatedSection key={plan.title} animation="fadeUp" delay={index * 100}>
                <div className={`p-8 rounded-2xl border transition-all duration-500 ${
                  plan.highlight 
                    ? 'bg-gradient-to-br from-teal-500 to-teal-600 border-teal-400' 
                    : 'bg-gray-800 border-gray-700 hover:border-teal-500'
                }`}>
                  <h3 className="text-2xl font-bold mb-4">{plan.title}</h3>
                  <p className={plan.highlight ? 'text-teal-100' : 'text-gray-400'}>{plan.description}</p>
                  <a
                    href={`https://wa.me/${companyInfo.whatsapp}?text=Olá! Gostaria de um orçamento de locação ${plan.title.toLowerCase()}.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`mt-6 inline-flex items-center gap-2 font-semibold ${
                      plan.highlight ? 'text-white' : 'text-teal-400'
                    }`}
                  >
                    Solicitar Orçamento
                    <ArrowRight size={18} />
                  </a>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="fadeRight">
              <span className="text-teal-500 font-semibold uppercase tracking-wider">Fale Conosco</span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-6">
                Solicite seu Orçamento de Locação
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Preencha o formulário e nossa equipe entrará em contato com as melhores condições.
              </p>
              
              <div className="flex items-center gap-4 p-4 bg-teal-50 rounded-xl">
                <Phone className="text-teal-600" size={24} />
                <div>
                  <p className="text-sm text-gray-500">Ou ligue agora</p>
                  <a href={`tel:${companyInfo.phone}`} className="text-lg font-bold text-gray-900">
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeLeft">
              <ContactForm 
                title="Orçamento de Locação"
                subtitle="Responderemos em até 24 horas."
              />
            </AnimatedSection>
          </div>
        </div>
      </section>
    </>
  );
};

export default RentalPage;
